import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Spinner,
  Pagination,
} from "react-bootstrap";

import {
  listDemosForAdmin,
  createDemo,
  updateDemo,
  deleteDemo,
  downloadDemo,
} from 'store/auth';
import { CACHED_FILE_BASE_URL } from '../../../commonConfig/ApiConfig';
import { LuDownload, LuFileText, LuVideo, LuExternalLink, LuEye, LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import MediaModal from "./MediaModal";

const DemoTab = () => {
  const [demos, setDemos] = useState([]);
  const [sortedDemos, setSortedDemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pagination - 8 items per row (4x2 grid)
  const [limit, setLimit] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Media Preview modal
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    type: "pdf",
    url: "",
    file: null,
    thumbnail: null,
    active: false,
    order_no: null,
  });

  // Handle file upload - automatically set active to true during creation
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !editMode) {
      // During creation, automatically set active when file is uploaded
      setFormData(prev => ({
        ...prev,
        file: file,
        active: true
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  // Handle URL change - automatically set active to true during creation for YouTube
  const handleUrlChange = (e) => {
    const url = e.target.value;
    if (url && !editMode) {
      // During creation, automatically set active when YouTube URL is entered
      setFormData(prev => ({
        ...prev,
        url: url,
        active: true
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        url: url
      }));
    }
  };

  // Fetch Demo List
  const fetchDemos = async () => {
    try {
      setLoading(true);
      const response = await listDemosForAdmin();
      const list = response?.data?.demos || [];
      setDemos(list);
      setSortedDemos(list);
    } catch (err) {
      console.error("Error fetching demos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemos();
  }, []);

  // Reorder functions
  const moveLeft = (index) => {
    if (index === 0) return; // Already at the start
    
    const newSorted = [...sortedDemos];
    const temp = newSorted[index];
    newSorted[index] = newSorted[index - 1];
    newSorted[index - 1] = temp;
    setSortedDemos(newSorted);
  };

  const moveRight = (index) => {
    if (index === sortedDemos.length - 1) return; // Already at the end
    
    const newSorted = [...sortedDemos];
    const temp = newSorted[index];
    newSorted[index] = newSorted[index + 1];
    newSorted[index + 1] = temp;
    setSortedDemos(newSorted);
  };

  // Save Sort Order
  const handleSaveSortOrder = async () => {
    if (!window.confirm("Save the current sort order? This will update all demos.")) return;

    try {
      setSaving(true);
      
      // Update each demo with new order_no
      for (let i = 0; i < sortedDemos.length; i++) {
        const item = sortedDemos[i];
        const FD = new FormData();
        FD.append("title", item.title);
        FD.append("type", item.type);
        FD.append("active", item.active);
        FD.append("order_no", i + 1);
        
        if (item.url) {
          FD.append("url", item.url);
        }
        
        await updateDemo(item.id, FD);
      }
      
      alert("Sort order saved successfully!");
      fetchDemos(); // Refresh from backend
    } catch (err) {
      console.error("Error saving sort order:", err);
      alert("Failed to save sort order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Pagination Helpers
  const totalPages = Math.ceil(sortedDemos.length / limit);
  const paginatedDemos = sortedDemos.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return <Pagination>{pages}</Pagination>;
  };

  // Open Add Modal
  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      id: null,
      title: "",
      type: "pdf",
      url: "",
      file: null,
      thumbnail: null,
      active: false,
      order_no: null,
    });
    setShowModal(true);
  };

  // Open Edit Modal
  const openEditModal = (item) => {
    setEditMode(true);
    setFormData({
      id: item.id,
      title: item.title,
      type: item.type,
      url: item.url || "",
      file: null,
      thumbnail: null,
      active: item.active !== undefined ? item.active : true,
      order_no: item.order_no,
    });
    setShowModal(true);
  };

  // Preview Media
  const openPreview = (item) => {
    setPreviewItem(item);
    setShowMediaModal(true);
  };

  // Delete Demo
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;

    try {
      await deleteDemo(id);
      fetchDemos();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // Submit Create/Update
  const handleSubmit = async () => {
    try {
      const FD = new FormData();
      FD.append("title", formData.title);
      FD.append("type", formData.type);
      FD.append("active", formData.active);

      if (formData.order_no !== null && formData.order_no !== undefined) {
        FD.append("order_no", formData.order_no);
      }

      if (formData.type === "youtube") {
        FD.append("url", formData.url);
      } else if (formData.file) {
        FD.append("file", formData.file);
      }

      if (formData.thumbnail) {
        FD.append("thumbnail", formData.thumbnail);
      }

      if (editMode) {
        await updateDemo(formData.id, FD);
      } else {
        await createDemo(FD);
      }

      setShowModal(false);
      fetchDemos();
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  // Extract YouTube video ID from URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : "";
  };

  const cardStyle = {
    borderRadius: '16px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    backgroundColor: 'white',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  };

  return (
    <div style={{ backgroundColor: 'white', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: '20px 0' }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', padding: '0 20px' }}>
          <h5 style={{ margin: 0, color: '#2c5282', fontWeight: '700', fontSize: '1.5rem' }}>Demos List</h5>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button 
              variant="primary" 
              onClick={handleSaveSortOrder}
              disabled={saving}
              style={{ 
                borderRadius: "8px",
                padding: "12px 24px",
                fontWeight: "600"
              }}
            >
              {saving ? "Saving..." : "Save Sort Order"}
            </Button>
            <Button 
              variant="primary" 
              onClick={openAddModal} 
              style={{ 
                borderRadius: "8px",
                padding: "12px 24px",
                fontWeight: "600"
              }}
            >
              + Add New Demo
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e2e8f0',
              borderTopColor: '#3182ce',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{ color: '#4a5568', fontSize: '1.125rem' }}>Loading demos...</p>
          </div>
        ) : paginatedDemos.length === 0 ? (
          <div style={{
            ...cardStyle,
            padding: '48px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: '#3182ce',
              fontSize: '32px'
            }}>📚</div>
            <h3 style={{ color: '#2c5282', fontWeight: '600', marginBottom: '12px', fontSize: '1.5rem' }}>
              No demo media found
            </h3>
          </div>
        ) : (
          <>
            {/* Cards Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '24px'
            }}>
              {paginatedDemos.map((item, paginatedIndex) => {
                const actualIndex = (currentPage - 1) * limit + paginatedIndex;
                const isFirst = actualIndex === 0;
                const isLast = actualIndex === sortedDemos.length - 1;

                return (
                  <div
                    key={item.id}
                    style={{...cardStyle, cursor: 'pointer'}}
                    onClick={() => {
                      if (item.type === "youtube") {
                        window.open(item.url, '_blank');
                      } else {
                        openPreview(item);
                      }
                    }}
                  >
                    {/* Media Section */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      {item.type === "youtube" && item.url ? (
                        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                          <iframe
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              border: 'none'
                            }}
                            src={getYouTubeEmbedUrl(item.url)}
                            title={item.title}
                            allowFullScreen
                          ></iframe>
                        </div>
                      ) : (
                        <div style={{ position: 'relative', height: '200px', overflow: 'hidden', backgroundColor: '#f1f5f9' }}>
                          {item.thumbnail_url ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{
                            display: item.thumbnail_url ? 'none' : 'flex',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#dbeafe',
                            gap: '12px'
                          }}>
                            {item.type === "pdf" ? (
                              <LuFileText size={64} style={{ color: '#3182ce', opacity: 0.6 }} />
                            ) : (
                              <LuVideo size={64} style={{ color: '#3182ce', opacity: 0.6 }} />
                            )}
                            <span style={{
                              color: '#1e40af',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              textAlign: 'center',
                              padding: '0 20px'
                            }}>
                              {item.type === "pdf" ? "PDF Document" : "Video Resource"}
                            </span>
                          </div>
                          {/* Active Status Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            zIndex: 10
                          }}>
                            {item.active ? 'Active' : 'Inactive'}
                          </div>

                          {/* File Type Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            zIndex: 10
                          }}>
                            {item.type === "pdf" ? <LuFileText size={14} /> : <LuVideo size={14} />}
                            {item.type?.toUpperCase()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content Section */}
                    <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <h5 style={{
                        color: '#2c5282',
                        fontWeight: '600',
                        marginBottom: '16px',
                        fontSize: '1.125rem',
                        lineHeight: '1.4',
                        flex: 1
                      }}>
                        {item.title}
                      </h5>

                      {/* Action Buttons - Edit and View */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(item);
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#3182ce',
                            border: '2px solid #3182ce',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.type === "youtube" && item.url) {
                              window.open(item.url, '_blank');
                            } else {
                              openPreview(item);
                            }
                          }}
                          style={{
                            backgroundColor: '#3182ce',
                            color: 'white',
                            border: '2px solid #3182ce',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          <LuEye className="me-2" style={{ display: 'inline' }} />
                          View
                        </button>
                      </div>

                      {/* Arrow Controls - Below buttons */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {/* Left Arrow */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLeft(actualIndex);
                          }}
                          disabled={isFirst}
                          style={{
                            backgroundColor: 'transparent',
                            color: isFirst ? '#cbd5e0' : '#64748b',
                            border: 'none',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isFirst ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: isFirst ? 0.3 : 0.6,
                            padding: 0
                          }}
                          onMouseEnter={(e) => {
                            if (!isFirst) e.currentTarget.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            if (!isFirst) e.currentTarget.style.opacity = '0.6';
                          }}
                          title="Move Left"
                        >
                          <LuChevronLeft size={20} />
                        </button>

                        {/* Right Arrow */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveRight(actualIndex);
                          }}
                          disabled={isLast}
                          style={{
                            backgroundColor: 'transparent',
                            color: isLast ? '#cbd5e0' : '#64748b',
                            border: 'none',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isLast ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: isLast ? 0.3 : 0.6,
                            padding: 0
                          }}
                          onMouseEnter={(e) => {
                            if (!isLast) e.currentTarget.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            if (!isLast) e.currentTarget.style.opacity = '0.6';
                          }}
                          title="Move Right"
                        >
                          <LuChevronRight size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
              {renderPagination()}
            </div>
          </>
        )}
      </div>

      {/* Enhanced Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header 
          closeButton 
          style={{ 
            borderBottom: "2px solid #e5e7eb",
            padding: "24px 32px"
          }}
        >
          <div>
            <Modal.Title style={{ fontSize: "24px", fontWeight: "700", color: "#1f2937" }}>
              {editMode ? "Edit Demo" : "Add New Demo"}
            </Modal.Title>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#6b7280" }}>
              {editMode ? "Update the demo information below" : "Fill in the details to add a new demo"}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: "32px" }}>
          <Form>
            {/* Title Input */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                Title <span style={{ color: "#dc2626" }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter demo title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                style={{
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: "2px solid #e5e7eb"
                }}
              />
            </Form.Group>

            {/* Type Select */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                Media Type <span style={{ color: "#dc2626" }}>*</span>
              </Form.Label>
              <Form.Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: "2px solid #e5e7eb"
                }}
              >
                <option value="pdf">PDF Document</option>
                <option value="mp4">MP4 Video</option>
                <option value="youtube">YouTube Link</option>
              </Form.Select>
            </Form.Group>

            {/* YouTube URL */}
            {formData.type === "youtube" && (
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                  YouTube URL <span style={{ color: "#dc2626" }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="https://youtube.com/watch?v=..."
                  value={formData.url}
                  onChange={handleUrlChange}
                  style={{
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "15px",
                    border: "2px solid #e5e7eb"
                  }}
                />
              </Form.Group>
            )}

            {/* File Upload */}
            {formData.type !== "youtube" && (
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                  Upload File {!editMode && <span style={{ color: "#dc2626" }}>*</span>}
                </Form.Label>
                <Form.Control
                  type="file"
                  accept={formData.type === "pdf" ? ".pdf" : "video/mp4"}
                  onChange={handleFileChange}
                  style={{
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "15px",
                    border: "2px solid #e5e7eb"
                  }}
                />
                {editMode && (
                  <Form.Text className="text-muted" style={{ fontSize: "13px" }}>
                    Leave empty to keep the existing file
                  </Form.Text>
                )}
              </Form.Group>
            )}

            {/* Thumbnail */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", marginBottom: "8px", fontSize: "14px" }}>
                Thumbnail <span style={{ color: "#6b7280", fontWeight: "400" }}>(Optional)</span>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.files[0] })}
                style={{
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: "2px solid #e5e7eb"
                }}
              />
            </Form.Group>

            {/* Active Status */}
            <div 
              style={{
                backgroundColor: "#f9fafb",
                padding: "16px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb"
              }}
            >
              <Form.Check 
                type="switch"
                id="active-switch"
                label={
                  <span style={{ fontWeight: "600", fontSize: "14px" }}>
                    Active (Show to users)
                  </span>
                }
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              <Form.Text className="text-muted" style={{ fontSize: "13px", display: "block", marginTop: "4px" }}>
                Only active demos will be visible to users
              </Form.Text>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "2px solid #e5e7eb", padding: "16px 32px" }}>
          <Button 
            variant="light" 
            onClick={() => setShowModal(false)}
            style={{
              borderRadius: "8px",
              padding: "10px 24px",
              fontWeight: "600",
              border: "2px solid #e5e7eb"
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            style={{
              borderRadius: "8px",
              padding: "10px 24px",
              fontWeight: "600",
              minWidth: "120px"
            }}
          >
            {editMode ? "Save Changes" : "Add Demo"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Media Preview Modal */}
      {previewItem && (
        <MediaModal
          isOpen={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          mediaType={previewItem.type}
          mediaUrl={
            previewItem.type === "youtube"
              ? previewItem.url
              : `${CACHED_FILE_BASE_URL}${previewItem.file_url}`
          }
          title={previewItem.title}
          onDownload={() =>
            downloadDemo(previewItem.id, previewItem.type, previewItem.title)
          }
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DemoTab;